
import React, { useState, useEffect } from "react";
import { toastSuccess } from "../utils/toast";

const NETWORK_FEE_GHSX = 0.5;

const BackChevron = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
    aria-label="Back"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  </button>
);

export function SendGhsx({
  socket,
  ghsxWallet,
  ghsxSocket,
  onBack,
  onSuccess,
}) {
  const [view, setView] = useState("send-input");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [sendError, setSendError] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [errors, setErrors] = useState({});

  const available = Number(ghsxWallet?.available ?? 0);

  const handleMax = () => {
    const maxAmount = Math.max(
      0,
      Number((available - NETWORK_FEE_GHSX).toFixed(2))
    );

    if (maxAmount > 0) {
      setSendAmount(String(maxAmount));
      setErrors((e) => ({ ...e, amount: null }));
      setSendError(null);
    }
  };

  const copyValue = async (value, label) => {
    await navigator.clipboard.writeText(String(value));
    toastSuccess(`${label} copied`);
  };

  /* ======================================================
     SOCKET RESPONSE HANDLERS
  ====================================================== */

  useEffect(() => {
    if (!socket) return;

    const handleSuccess = (payload) => {
      setSendLoading(false);
      setSendResult(payload);
      setView("send-success");
      onSuccess?.();
    };

    const handleError = ({ error }) => {
      setSendLoading(false);
      setSendError(error || "Transfer failed.");
    };

    socket.on("ghsx-transfer-success", handleSuccess);
    socket.on("ghsx-transfer-error", handleError);

    return () => {
      socket.off("ghsx-transfer-success", handleSuccess);
      socket.off("ghsx-transfer-error", handleError);
    };
  }, [socket, onSuccess]);

  /* ======================================================
     INPUT SCREEN
  ====================================================== */

  if (view === "send-input") {
    const numeric = Number(sendAmount);
    const hasValidInput =
      recipientAddress.trim() &&
      sendAmount &&
      numeric > 0;

    const handlePreview = () => {
      const nextErrors = {};

      if (!recipientAddress.trim()) {
        nextErrors.recipient =
          "Enter recipient GHSX address";
      }

      if (!sendAmount || numeric <= 0) {
        nextErrors.amount =
          "Enter a valid amount";
      } else if (
        numeric + NETWORK_FEE_GHSX >
        available
      ) {
        nextErrors.amount =
          `Insufficient balance. You have ${available.toFixed(
            2
          )} GHSX (includes ${NETWORK_FEE_GHSX} fee).`;
      }

      setErrors(nextErrors);

      if (Object.keys(nextErrors).length) return;

      setView("send-confirm");
    };

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <BackChevron onClick={onBack} />
            <h2 className="text-base font-semibold text-orange-400">
              Send GHSX
            </h2>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              Available Balance
            </div>

            <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">
              {available.toFixed(2)} GHSX
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold">
                Recipient GHSX Address
              </label>

              <input
                type="text"
                value={recipientAddress}
                onChange={(e) => {
                  setRecipientAddress(e.target.value);
                  setErrors((er) => ({
                    ...er,
                    recipient: null,
                  }));
                }}
                placeholder="Paste GHSX address"
                className="w-full rounded-lg border px-4 py-2.5"
              />

              {errors.recipient && (
                <div className="mt-1 text-xs text-red-500">
                  {errors.recipient}
                </div>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold">
                Amount (GHSX)
              </label>

              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={sendAmount}
                  onChange={(e) => {
                    const raw = e.target.value;

                    if (
                      raw === "" ||
                      /^\d+(\.\d{0,2})?$/.test(raw)
                    ) {
                      setSendAmount(raw);
                      setErrors((er) => ({
                        ...er,
                        amount: null,
                      }));
                    }
                  }}
                  className="w-full rounded-lg border px-4 py-2.5 pr-16"
                />

                {available >
                  NETWORK_FEE_GHSX && (
                  <button
                    type="button"
                    onClick={handleMax}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-green-600"
                  >
                    MAX
                  </button>
                )}
              </div>

              {errors.amount && (
                <div className="mt-1 text-xs text-red-500">
                  {errors.amount}
                </div>
              )}
            </div>

            <button
              onClick={handlePreview}
              disabled={!hasValidInput}
              className="w-full rounded-lg bg-orange-400 py-2.5 font-semibold text-white"
            >
              Preview Transfer
            </button>
          </div>

          <div className="text-xs text-neutral-500">
            Network fee:{" "}
            {NETWORK_FEE_GHSX.toFixed(2)} GHSX
          </div>
        </div>
      </div>
    );
  }

  /* ======================================================
     CONFIRM SCREEN
  ====================================================== */

  if (view === "send-confirm") {
    const numeric = Number(sendAmount);
    const total = Number(
      (numeric + NETWORK_FEE_GHSX).toFixed(2)
    );

    const afterSend = Number(
      (available - total).toFixed(2)
    );

    const handleConfirm = () => {
      setSendLoading(true);
      setSendError(null);

      ghsxSocket.transferGhsx({
        recipientAddress:
          recipientAddress.trim(),
        amount: numeric,
      });
    };

    return (
      <div className="flex h-full flex-col bg-white dark:bg-neutral-900">
        <div className="border-b p-4">
          <div className="flex items-center gap-3">
            <BackChevron
              onClick={() =>
                setView("send-input")
              }
            />
            <h2 className="font-semibold text-orange-400">
              Confirm Transfer
            </h2>
          </div>
        </div>

        <div className="flex-1 space-y-4 p-6">
          <div className="rounded-xl border divide-y">
            <div className="flex justify-between p-3 text-sm">
              <span>To</span>

              <span className="font-mono text-xs text-right break-all max-w-[55%]">
                {recipientAddress.trim()}
              </span>
            </div>

            <div className="flex justify-between p-3 text-sm">
              <span>Amount</span>

              <strong>
                {numeric.toFixed(2)} GHSX
              </strong>
            </div>

            <div className="flex justify-between p-3 text-sm">
              <span>Network fee</span>

              <span>
                {NETWORK_FEE_GHSX.toFixed(2)} GHSX
              </span>
            </div>

            <div className="flex justify-between p-3 text-sm font-semibold">
              <span>Total deducted</span>

              <span className="text-orange-500">
                {total.toFixed(2)} GHSX
              </span>
            </div>

            <div className="flex justify-between p-3 text-sm">
              <span>Available after</span>

              <span>
                {afterSend.toFixed(2)} GHSX
              </span>
            </div>
          </div>

          {sendError && (
            <div className="text-sm text-red-500">
              {sendError}
            </div>
          )}
        </div>

        <div className="border-t p-4 flex gap-3">
          <button
            onClick={() =>
              setView("send-input")
            }
            disabled={sendLoading}
            className="flex-1 rounded-lg border py-2.5"
          >
            Edit
          </button>

          <button
            onClick={handleConfirm}
            disabled={sendLoading}
            className="flex-1 rounded-lg bg-orange-400 py-2.5 text-white"
          >
            {sendLoading
              ? "Sending..."
              : "Confirm Send"}
          </button>
        </div>
      </div>
    );
  }

  /* ======================================================
     SUCCESS SCREEN
  ====================================================== */

  if (view === "send-success" && sendResult) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-neutral-900">
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-orange-500 text-3xl">
            ✓
          </div>

          <h3 className="text-xl font-bold">
            {Number(sendResult.amount).toFixed(2)} GHSX sent
          </h3>

          <p className="mt-2 text-sm text-neutral-500">
            Transfer completed successfully.
          </p>

          <div className="mt-6 w-full rounded-xl border divide-y">
            <div className="flex justify-between p-3 text-sm">
              <span>To address</span>

              <span className="font-mono text-xs text-right break-all max-w-[55%]">
                {sendResult.recipientAddress}
              </span>
            </div>

            <div className="flex justify-between p-3 text-sm">
              <span>Amount</span>

              <span>
                {Number(sendResult.amount).toFixed(2)} GHSX
              </span>
            </div>

            <div className="flex justify-between p-3 text-sm">
              <span>Network fee</span>

              <span>
                {Number(
                  sendResult.fee ??
                    NETWORK_FEE_GHSX
                ).toFixed(2)} GHSX
              </span>
            </div>

            <div className="flex justify-between p-3 text-sm">
              <span>Transaction ID</span>

              <span
                className="font-mono text-xs text-orange-500 cursor-pointer"
                onClick={() =>
                  copyValue(
                    sendResult.txHash,
                    "Transaction ID"
                  )
                }
              >
                {sendResult.txHash
                  ? `${sendResult.txHash.slice(
                      0,
                      16
                    )}…`
                  : "—"}
              </span>
            </div>
          </div>

          <button
            onClick={onBack}
            className="mt-8 w-full rounded-lg bg-orange-400 py-2.5 font-semibold text-white"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return null;
}